import { BaseProvider } from "./base.provider";
import type { Paginated } from "./waitlist.provider";

export type CounterPeriod = "all" | "month" | "day";

// Omitted from a request, the backend reads its own UNIVERSE.
export type CounterUniverse = "dev" | "staging" | "prod";

export interface Counter {
  universe: string;
  counter_name: string;
  user_id: string | null;
  period: CounterPeriod;
  // "" for period "all", YYYY-MM for "month", YYYY-MM-DD for "day" (UTC).
  bucket: string;
  value: number;
  updated_at: string;
  expire_at?: string;
}

export type PaginatedCounters = Paginated<Counter>;

export interface CounterSeries {
  scope: "user" | "global";
  period: CounterPeriod;
  doc_count: number;
  first_bucket: string;
  last_bucket: string;
  last_updated_at: string;
}

export interface CounterDefinition {
  name: string;
  periods: CounterPeriod[];
  has_global: boolean;
  has_per_user: boolean;
  first_bucket: string;
  last_bucket: string;
  doc_count: number;
  last_updated_at: string;
  series: CounterSeries[];
  universes: string[];
  in_current_universe: boolean;
}

export interface CounterUserRank {
  rank: number;
  user_id: string;
  // null for deleted or anonymized users.
  username: string | null;
  total: number;
  rows: number;
  last_updated_at: string;
}

export type CounterUserSort = "value_desc" | "value_asc";

export interface CounterUsersParams {
  universe?: CounterUniverse;
  name: string;
  period: CounterPeriod;
  from?: string;
  to?: string;
  sort?: CounterUserSort;
  page?: number;
}

export interface CounterTotal {
  bucket: string;
  total: number;
  doc_count: number;
}

// Omitted, rows come newest bucket first, then by name and user.
export type CounterRowSort = "value_desc" | "value_asc" | "updated_desc";

export interface CounterListParams {
  universe?: CounterUniverse;
  sort?: CounterRowSort;
  name?: string;
  user_id?: string;
  global?: boolean;
  period?: CounterPeriod;
  from?: string;
  to?: string;
  page?: number;
}

export interface CounterTotalsParams {
  universe?: CounterUniverse;
  name: string;
  period: CounterPeriod;
  user_id?: string;
  global?: boolean;
  from?: string;
  to?: string;
}

function toQuery(params: CounterListParams | CounterTotalsParams | CounterUsersParams): string {
  const p = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    p.set(key, String(value));
  }
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

class CountersProvider extends BaseProvider {
  list(params: CounterListParams = {}): Promise<PaginatedCounters> {
    return this.request<PaginatedCounters>(`/v1/admin/counters${toQuery(params)}`);
  }

  listForUser(
    userId: string,
    params: Omit<CounterListParams, "user_id" | "global"> = {},
  ): Promise<PaginatedCounters> {
    return this.request<PaginatedCounters>(
      `/v1/admin/users/${encodeURIComponent(userId)}/counters${toQuery(params)}`,
    );
  }

  // Without a universe, counts and series are summed across all universes and
  // won't match what the row routes return.
  getDefinitions(universe?: CounterUniverse): Promise<CounterDefinition[]> {
    return this.request<CounterDefinition[]>(
      `/v1/admin/counters/definitions${toQuery({ universe })}`,
    );
  }

  getTotals(params: CounterTotalsParams): Promise<CounterTotal[]> {
    return this.request<CounterTotal[]>(`/v1/admin/counters/totals${toQuery(params)}`);
  }

  getUserRanking(params: CounterUsersParams): Promise<Paginated<CounterUserRank>> {
    return this.request<Paginated<CounterUserRank>>(
      `/v1/admin/counters/by-user${toQuery(params)}`,
    );
  }
}

export const countersProvider = new CountersProvider();
